import { useEffect, useRef, useState } from 'react'
import * as actions from '../../../store/slices'
import useDebounce from '../../../hooks/useDebounce'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { getUrlParams } from '../../../utils/helpers/common'
import constants from '../../../constants'
import { STATE_PAGE_SIZE, OTHER_PAGE_SIZE } from '../../../constants/common'
import { useParams } from 'react-router-dom'

const useGetStateAndCity = ({ onChange, source, initialSelection }) => {
  const location = useLocation()
  const params = getUrlParams()
  const { variant } = useParams()
  // Determine if this is a CGM flow
  const isCgmFlow = Boolean(params?.cgm) || location.state?.isCgmFlow
  const cgmType = params?.cgm

  // Check if it's specifically diasensCGM flow
  const isDiasensCGM =
    isCgmFlow &&
    (cgmType === constants.CGM_TYPE.DIASENS ||
      cgmType === constants.CGM_TYPE.GLUCOLIVE)

  const [activeSection, setActiveSection] = useState(null)
  const [selectedState, setSelectedState] = useState('')
  const [selectedCity, setSelectedCity] = useState('')
  const [selectedHospital, setSelectedHospital] = useState('')
  const [selectedDoctor, setSelectedDoctor] = useState('')
  const [list, setList] = useState([])
  const [doctorName, setDoctorName] = useState('')
  const [addDoctor, setAddDoctor] = useState(false)
  const [hospitalName, setHospitalName] = useState('')
  const [addHospital, setAddHospital] = useState(false)

  const dispatch = useDispatch()

  // State list pagination (used by DoctorLocation state bottom sheet)
  const [statePage, setStatePage] = useState(1)
  const [hasMoreStates, setHasMoreStates] = useState(true)
  const [paginatedStateList, setPaginatedStateList] = useState([])
  const stateRequestRef = useRef({ isLoadMore: false })

  const [otherPages, setOtherPages] = useState({
    city: 1,
    hospital: 1,
    doctor: 1,
  })
  const [otherHasMore, setOtherHasMore] = useState({
    city: true,
    hospital: true,
    doctor: true,
  })

  const cityPage = otherPages.city
  const hasMoreCities = otherHasMore.city
  const hospitalPage = otherPages.hospital
  const hasMoreHospitals = otherHasMore.hospital
  const doctorPage = otherPages.doctor
  const hasMoreDoctors = otherHasMore.doctor

  const cityRequestRef = useRef({ isLoadMore: false })
  const prevCityListLenRef = useRef(0)
  const hospitalRequestRef = useRef({ isLoadMore: false })
  const prevHospitalListLenRef = useRef(0)
  const doctorRequestRef = useRef({ isLoadMore: false })
  const prevDoctorListLenRef = useRef(0)
  const {
    stateList: {
      data: stateList,
      loading: stateListLoading,
      error: stateListError,
    },
    doctorCityList: {
      data: cityList,
      pagination: cityPagination,
      loading: cityListLoading,
      error: cityListError,
    },
    hospitalsInCityList: {
      data: hospitalList,
      loading: hospitalListLoading,
      error: hospitalListError,
    },
    doctorsListInHospitalList: {
      data: doctorList,
      loading: doctorListLoading,
      error: doctorListError,
    },
    addDoctor: {
      data: addDoctorData,
      loading: addDoctorLoading,
      error: addDoctorError,
      success: addDoctorSuccess,
    },
    addHospital: {
      data: addHospitalData,
      loading: addHospitalLoading,
      error: addHospitalError,
      success: addHospitalSuccess,
    },
  } = useSelector((state) => state.carePlan)

  const getStateList = ({ search = '', page = 1, isLoadMore = false } = {}) => {
    stateRequestRef.current = { isLoadMore, page }
    setStatePage(page)

    // Reset local pagination for non-load-more fetches
    if (!isLoadMore) {
      setHasMoreStates(true)
      setPaginatedStateList([])
    }

    // NOTE: backend pagination params live in POST payload for this endpoint.
    dispatch(
      actions.getStateListRequest({
        search,
        pageNo: page,
        pageSize: STATE_PAGE_SIZE,
        isLoadMore,
      }),
    )
  }
  const getCityList = (stateId, search = '', page = 1, isLoadMore = false) => {
    cityRequestRef.current = { isLoadMore }
    setOtherPages((prev) => ({ ...prev, city: page }))
    if (!isLoadMore) {
      setOtherHasMore((prev) => ({ ...prev, city: true }))
    }
    dispatch(
      actions.getDoctorCityListRequest({
        state_id: stateId,
        page: page,
        // specialization: 'oncology', //TODO: need to discuss with backend
        // source: 'drug p', //TODO: need to discuss with backend
        search_param: search,
        isLoadMore: isLoadMore,
      }),
    )
  }
  const getHospitalsInCityList = (
    cityId,
    search = '',
    page = 1,
    isLoadMore = false,
  ) => {
    hospitalRequestRef.current = { isLoadMore }
    setOtherPages((prev) => ({ ...prev, hospital: page }))
    if (!isLoadMore) {
      setOtherHasMore((prev) => ({ ...prev, hospital: true }))
      prevHospitalListLenRef.current = 0
    }
    dispatch(
      actions.getHospitalsInCityListRequest({
        city_id: cityId,
        page: page,
        search_param: search,
        isLoadMore: isLoadMore,
      }),
    )
  }
  const getDoctorsListInHospitalList = (
    hospitalId,
    search = '',
    page = 1,
    isLoadMore = false,
  ) => {
    doctorRequestRef.current = { isLoadMore }
    setOtherPages((prev) => ({ ...prev, doctor: page }))
    if (!isLoadMore) {
      setOtherHasMore((prev) => ({ ...prev, doctor: true }))
    }
    dispatch(
      actions.getDoctorsListInHospitalListRequest({
        clinic_id: hospitalId,
        page: page,
        search_param: search,
        isLoadMore: isLoadMore,
        ...(source && { source }),
      }),
    )
  }

  const [formFields, setFormFields] = useState({
    state: '',
    city: '',
    hospital: '',
    doctor: '',
  })
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    if (!initialSelection) return

    if (initialSelection?.selectedState) {
      setSelectedState(initialSelection.selectedState)
    }
    if (initialSelection?.selectedCity) {
      setSelectedCity(initialSelection.selectedCity)
    }
    if (initialSelection?.selectedHospital) {
      setSelectedHospital(initialSelection.selectedHospital)
    }
    if (initialSelection?.selectedDoctor) {
      setSelectedDoctor(initialSelection.selectedDoctor)
    }
  }, [initialSelection])

  useEffect(() => {
    getStateList({ search: '', page: 1 })
  }, [])

  const handleChange = (key, value) => {
    setFormFields({ ...formFields, [key]: value })
  }

  const handleSearch = (query) => {
    if (activeSection === 'city') {
      getCityList(selectedState?.state_master_id, query, 1, false) // Reset to page 1 on search
    } else if (activeSection === 'state') {
      getStateList({ search: query, page: 1, isLoadMore: false })
    } else if (activeSection === 'hospital') {
      getHospitalsInCityList(selectedCity?.city_master_id, query, 1, false)
    } else if (activeSection === 'doctor') {
      getDoctorsListInHospitalList(
        selectedHospital?.clinic_master_id,
        query,
        1,
        false,
      )
    }
  }

  const debouncedSearch = useDebounce(handleSearch, 1000)

  const handleOnSearch = (event) => {
    const query = event.target.value
    setSearchQuery(query)
    debouncedSearch(query)
  }

  const handleOptionClick = (item) => {
    if (activeSection === 'doctor') {
      onChange(activeSection, item?.name)
      setSelectedDoctor(item)
    } else if (activeSection === 'hospital') {
      setSelectedHospital(item)
      setSelectedDoctor(() => '')
      onChange(activeSection, item?.clinic_name)
    } else if (activeSection === 'city') {
      setSelectedCity(item)
      setSelectedHospital(() => '')
      setSelectedDoctor(() => '')
      onChange(activeSection, item?.city_name)
    } else if (activeSection === 'state') {
      if (selectedState?.state_master_id !== item?.state_master_id) {
        setSelectedState(item)
        setSelectedCity(() => '')
        setSelectedHospital(() => '')
        setSelectedDoctor(() => '')
        onChange(activeSection, item?.state_name)
      }
    }
    setAddDoctor(false)
    setActiveSection(null)
  }

  const handleActiveSession = (section) => {
    if (activeSection !== section) {
      setActiveSection(section)
      setSearchQuery('')
      setList([])

      if (section === 'state') {
        // Avoid duplicate page-1 call when first page is already available.
        if (!paginatedStateList?.length) {
          getStateList({ search: '', page: 1, isLoadMore: false })
        } else {
          setList(paginatedStateList)
        }
      } else if (section === 'city') {
        getCityList(selectedState?.state_master_id, '', 1, false)
      } else if (section === 'hospital') {
        getHospitalsInCityList(selectedCity?.city_master_id, '', 1, false)
      } else if (section === 'doctor') {
        getDoctorsListInHospitalList(
          selectedHospital?.clinic_master_id,
          '',
          1,
          false,
        )
      }
    }
  }
  const handleOnCloseSheet = () => {
    setActiveSection(null)
    setAddHospital(false)
    setAddDoctor(false)
    setHospitalName('')
    setDoctorName('')
    setList([])
  }

  useEffect(() => {
    if (activeSection === 'state') {
      setList(paginatedStateList)
    } else if (activeSection === 'city') {
      setList(cityList)
    } else if (activeSection === 'hospital') {
      setList(hospitalList)
    } else if (activeSection === 'doctor') {
      setList(doctorList)
    }
  }, [activeSection, paginatedStateList, cityList, hospitalList, doctorList])

  // Keep local paginated state list in sync with redux `stateList` fetches.
  useEffect(() => {
    if (stateListLoading) return

    const items = Array.isArray(stateList) ? stateList : []
    const { isLoadMore, page } = stateRequestRef.current

    let nextPageItems = []
    if (!isLoadMore) {
      nextPageItems = items.slice(0, STATE_PAGE_SIZE)
    } else {
      const start = (page - 1) * STATE_PAGE_SIZE
      // If backend returns only the requested page, just use it directly.
      if (items.length <= STATE_PAGE_SIZE) {
        nextPageItems = items
      } else {
        nextPageItems = items.slice(start, start + STATE_PAGE_SIZE)
      }
    }

    setPaginatedStateList((prev) => {
      const combined = !isLoadMore ? nextPageItems : [...prev, ...nextPageItems]

      // Prevent duplicate state items when the backend response overlaps
      // across page requests (or when multiple requests race).
      const seen = new Set()
      return combined.filter((item) => {
        const id = item?.state_master_id
        if (!id) return true
        if (seen.has(id)) return false
        seen.add(id)
        return true
      })
    })

    // Stop when the backend returns less than the expected page size.
    setHasMoreStates(nextPageItems.length === STATE_PAGE_SIZE)
  }, [stateList, stateListLoading])

  useEffect(() => {
    if (cityListLoading) return
    const items = Array.isArray(cityList) ? cityList : []

    const currentLen = items.length
    const { isLoadMore } = cityRequestRef.current
    const appended = currentLen - prevCityListLenRef.current

    // For fresh fetch: allow more only if we received a full page.
    // For load-more: allow more only if the last request appended a full page.
    const hasMoreNow = isLoadMore
      ? appended >= OTHER_PAGE_SIZE
      : currentLen >= OTHER_PAGE_SIZE

    setOtherHasMore((prev) => ({ ...prev, city: hasMoreNow }))
    prevCityListLenRef.current = currentLen
  }, [cityList, cityListLoading])

  useEffect(() => {
    if (hospitalListLoading) return
    const items = Array.isArray(hospitalList) ? hospitalList : []
    const currentLen = items.length
    const { isLoadMore } = hospitalRequestRef.current
    const appended = currentLen - prevHospitalListLenRef.current

    const hasMoreNow = isLoadMore
      ? appended >= OTHER_PAGE_SIZE
      : currentLen >= OTHER_PAGE_SIZE

    setOtherHasMore((prev) => ({ ...prev, hospital: hasMoreNow }))
    prevHospitalListLenRef.current = currentLen
  }, [hospitalList, hospitalListLoading])

  useEffect(() => {
    if (doctorListLoading) return
    const items = Array.isArray(doctorList) ? doctorList : []
    const currentLen = items.length
    const { isLoadMore } = doctorRequestRef.current
    const appended = currentLen - prevDoctorListLenRef.current

    const hasMoreNow = isLoadMore
      ? appended >= OTHER_PAGE_SIZE
      : currentLen >= OTHER_PAGE_SIZE

    setOtherHasMore((prev) => ({ ...prev, doctor: hasMoreNow }))
    prevDoctorListLenRef.current = currentLen
  }, [doctorList, doctorListLoading])

  // Handle Add Doctor
  const handleAddDoctor = () => {
    const payload = {
      state_id: selectedState?.state_master_id,
      city_id: selectedCity?.city_master_id,
      state_name: selectedState?.state_name,
      city_name: selectedCity?.city_name,
      clinic_id: selectedHospital?.clinic_master_id,
      dr_name: doctorName,
      source: source || 'Drug P',
      specialization: source ? '' : 'Oncology',
      ...(variant && { medicineType: variant.toUpperCase() }),
    }
    dispatch(actions.addDoctorRequest(payload))
  }

  const handleAddHospital = () => {
    const payload = {
      state_id: selectedState?.state_master_id,
      city_id: selectedCity?.city_master_id,
      state_name: selectedState?.state_name,
      city_name: selectedCity?.city_name,
      clinic_name: hospitalName,
      source: source || 'Drug P',
      specialization: source ? '' : 'Oncology',
      ...(variant && { medicineType: variant.toUpperCase() }),
    }
    dispatch(actions.addHospitalRequest(payload))
  }
  useEffect(() => {
    if (
      !addDoctorLoading &&
      !addDoctorError &&
      addDoctorSuccess &&
      addDoctorData?.length > 0
    ) {
      handleOptionClick(addDoctorData[0])
      setAddDoctor(false)
      setDoctorName('')
    }
  }, [addDoctorLoading, addDoctorError, addDoctorSuccess, addDoctorData])

  useEffect(() => {
    if (
      !addHospitalLoading &&
      !addHospitalError &&
      addHospitalSuccess &&
      addHospitalData?.length > 0
    ) {
      handleOptionClick(addHospitalData[0])
      setAddHospital(false)
      setHospitalName('')
    }
  }, [
    addHospitalLoading,
    addHospitalError,
    addHospitalSuccess,
    addHospitalData,
  ])

  // Load more cities for pagination
  const loadMoreCities = () => {
    if (
      // cityPagination?.has_more &&
      hasMoreCities &&
      !cityListLoading &&
      selectedState?.state_master_id
    ) {
      const nextPage = cityPage + 1
      getCityList(selectedState?.state_master_id, searchQuery, nextPage, true)
    }
  }

  const loadMoreHospitals = () => {
    if (
      !hasMoreHospitals ||
      hospitalListLoading ||
      !selectedCity?.city_master_id
    ) {
      return
    }
    const nextPage = hospitalPage + 1
    getHospitalsInCityList(
      selectedCity?.city_master_id,
      searchQuery,
      nextPage,
      true,
    )
  }

  // Load more states for pagination
  const loadMoreStates = () => {
    if (!hasMoreStates || stateListLoading) return
    const nextPage = statePage + 1
    getStateList({ search: searchQuery, page: nextPage, isLoadMore: true })
  }

  const loadMoreDoctors = () => {
    if (
      !hasMoreDoctors ||
      doctorListLoading ||
      !selectedHospital?.clinic_master_id
    ) {
      return
    }
    const nextPage = doctorPage + 1
    getDoctorsListInHospitalList(
      selectedHospital?.clinic_master_id,
      searchQuery,
      nextPage,
      true,
    )
  }

  return {
    cityList,
    stateList,
    searchQuery,
    selectedCity,
    activeSection,
    selectedState,
    cityListError,
    stateListError,
    cityListLoading,
    stateListLoading,
    handleChange,
    handleOnSearch,
    handleOptionClick,
    setActiveSection,
    setSelectedState,
    setSelectedCity,
    setSelectedHospital,
    setSelectedDoctor,
    handleActiveSession,
    handleOnCloseSheet,
    selectedHospital,
    selectedDoctor,
    list,
    handleAddDoctor,
    doctorName,
    setDoctorName,
    addDoctor,
    setAddDoctor,
    addDoctorLoading,
    handleAddHospital,
    addHospital,
    setAddHospital,
    addHospitalLoading,
    hospitalName,
    setHospitalName,
    cityPagination,
    loadMoreCities,
    hasMoreCities,
    loadMoreHospitals,
    hasMoreHospitals,
    loadMoreDoctors,
    hasMoreDoctors,
    hasMoreStates,
    loadMoreStates,
    isCgmFlow,
    cgmType,
    isDiasensCGM,
    loading:
      stateListLoading ||
      cityListLoading ||
      hospitalListLoading ||
      doctorListLoading,
    error:
      stateListError || cityListError || hospitalListError || doctorListError,
  }
}

export default useGetStateAndCity
